/**
 * Gift calculator math (CHAP-301) - shared by client + server.
 *
 * Donors give from ASSETS, not income (CHAP-286, Nick LCAEF + Eric Handler
 * transcripts): the calculator makes the after-tax cost of a gift concrete
 * per giving vehicle so larger asks land. Pure + dependency-free so the
 * client computes live as inputs change, the server recomputes on save
 * (server-authoritative outputs), and every vehicle's math is unit-testable
 * without a DB.
 *
 * Every figure is an EDUCATIONAL ESTIMATE for planning conversations, not
 * tax advice (see GIFT_CALCULATOR_DISCLAIMER, rendered on the surface).
 *
 * Educational-space deductibility (CHAP-231): when the tenant has a
 * tax_deductibility_rulings row, its percentage scales the deduction line
 * (a ruling that N% of the project is educational space means N% of a gift
 * is treated as deductible). Capital-gains avoidance is NOT scaled (the
 * donor avoids the gain by giving the asset regardless); QCDs are NOT
 * scaled (income exclusion is all-or-nothing on charity qualification) -
 * both simplifications are surfaced in the per-vehicle notes.
 */

/** Giving vehicles, v1 set per CHAP-301 AC. */
export const GIFT_ASSET_TYPES = [
  'cash',
  'stock',
  'daf',
  'qcd',
  'real_estate',
  'business_interest',
] as const;

export type GiftAssetType = (typeof GIFT_ASSET_TYPES)[number];

export interface GiftVehicleMeta {
  key: GiftAssetType;
  label: string;
  /** Vehicle benefits from a cost-basis input (capital-gains avoidance). */
  usesBasis: boolean;
  /** Vehicle needs the donor's age (QCD gating). */
  usesAge: boolean;
}

export const GIFT_VEHICLES: readonly GiftVehicleMeta[] = [
  { key: 'cash', label: 'Cash', usesBasis: false, usesAge: false },
  { key: 'stock', label: 'Appreciated stock', usesBasis: true, usesAge: false },
  { key: 'daf', label: 'DAF grant', usesBasis: true, usesAge: false },
  { key: 'qcd', label: 'QCD from IRA (age 70.5+)', usesBasis: false, usesAge: true },
  { key: 'real_estate', label: 'Real estate', usesBasis: true, usesAge: false },
  { key: 'business_interest', label: 'Business interest', usesBasis: true, usesAge: false },
];

/** IRS minimum age for a Qualified Charitable Distribution. */
export const QCD_MIN_AGE = 70.5;

/** Rendered under the calculator. Compliance AC: educational estimate, not tax advice. */
export const GIFT_CALCULATOR_DISCLAIMER =
  'Educational estimate only, for planning conversations. This is not tax, legal, or ' +
  'financial advice; deduction limits, AGI caps, and eligibility depend on the donor\'s ' +
  'full situation. Confirm any gift with a qualified tax advisor.';

/**
 * Infer a long-term capital-gains rate from the donor's marginal ordinary
 * rate. Deliberately coarse (bracket thresholds shift yearly); the assumed
 * rate is echoed in the outputs so it is never hidden. NIIT (3.8%) is not
 * modeled.
 */
export function inferLtcgRatePct(marginalRatePct: number): number {
  if (marginalRatePct < 22) return 0;
  if (marginalRatePct < 32) return 15;
  return 20;
}

export interface GiftInput {
  assetType: GiftAssetType;
  /** Fair market value of the gift, in dollars. */
  amount: number;
  /** Cost basis in dollars, where relevant (stock / DAF / real estate / business interest). */
  costBasis?: number | null;
  /** Donor's marginal ordinary income tax rate, percent (e.g. 35). */
  marginalRatePct: number;
  /** Donor's age in years; only consulted for QCD gating. */
  age?: number | null;
}

/** Snapshot of the tenant's educational-space ruling, when one exists. */
export interface TaxRulingRef {
  /** Deductible percentage from tax_deductibility_rulings.percentage (0..100). */
  percentage: number;
}

export interface GiftOutcome {
  eligible: boolean;
  ineligibleReason: string | null;
  /** Charitable deduction before any educational-space ruling. 0 for QCD. */
  deduction: number;
  /** Deduction after the educational-space ruling percentage (equals deduction when no ruling). */
  deductionAfterRuling: number;
  /** Ruling percentage actually applied to the deduction line, or null. */
  rulingPercentageApplied: number | null;
  /** Income excluded from AGI (QCD only; 0 for other vehicles). */
  incomeExcluded: number;
  /** Tax saved via the deduction (or QCD income exclusion) at the marginal rate. */
  taxSavings: number;
  /** Unrealized gain sheltered by giving the asset instead of selling it. */
  appreciationSheltered: number;
  /** Tax avoided on that gain at the assumed LTCG rate. */
  capitalGainsTaxAvoided: number;
  /** LTCG rate assumed (echoed so the estimate is transparent). */
  assumedLtcgRatePct: number;
  /** Out-of-pocket cost: amount - taxSavings - capitalGainsTaxAvoided. */
  netCost: number;
  /** Per-vehicle guidance notes (appraisal / advisory / limit notes per AC). */
  notes: string[];
}

const ZERO_OUTCOME: Omit<GiftOutcome, 'eligible' | 'ineligibleReason' | 'notes'> = {
  deduction: 0,
  deductionAfterRuling: 0,
  rulingPercentageApplied: null,
  incomeExcluded: 0,
  taxSavings: 0,
  appreciationSheltered: 0,
  capitalGainsTaxAvoided: 0,
  assumedLtcgRatePct: 0,
  netCost: 0,
};

function ineligible(reason: string, notes: string[] = []): GiftOutcome {
  return { ...ZERO_OUTCOME, eligible: false, ineligibleReason: reason, notes };
}

const VEHICLE_NOTES: Record<GiftAssetType, string[]> = {
  cash: [
    'Deduction value assumes the donor itemizes (vs the standard deduction).',
    'Cash gifts to public charities are generally deductible up to 60% of AGI; excess carries forward up to 5 years.',
  ],
  stock: [
    'Assumes stock held long-term (more than 1 year); short-term holdings deduct at cost basis instead of fair market value.',
    'Neither the donor nor the charity pays capital gains tax on the appreciation.',
    'Appreciated-asset gifts are generally deductible up to 30% of AGI; excess carries forward up to 5 years.',
  ],
  daf: [
    'The deduction is taken when the DAF is funded; the grant from the DAF to the chapter carries no additional deduction.',
    'Funding the DAF with appreciated stock also avoids capital gains (enter the cost basis above).',
    'Useful for bunching several years of giving into a single deduction year.',
  ],
  qcd: [
    'The distribution goes directly from the IRA custodian to the charity and is excluded from income; no itemizing needed.',
    'Counts toward the donor\'s required minimum distribution (RMD).',
    'Annual QCD limit is $108,000 per person (2025, indexed annually).',
    'The receiving entity must be a qualified public charity; confirm with the foundation before directing a QCD.',
  ],
  real_estate: [
    'Appraisal note: a qualified appraisal (IRS Form 8283) is required for property gifts over $5,000.',
    'Deductible at fair market value up to 30% of AGI when held more than 1 year.',
    'Depreciation recapture on rental property can reduce the benefit; not modeled here.',
    'Confirm the foundation can accept, hold, and dispose of the property before committing.',
  ],
  business_interest: [
    'Advisory note: gifts of closely held business interests require a qualified appraisal and coordination between the donor\'s tax advisor and the foundation.',
    'S corporation shares, partnership interests, and UBIT exposure add complexity; not modeled here.',
    'Deductible at appraised fair market value up to 30% of AGI when held more than 1 year.',
  ],
};

const round = (n: number): number => Math.round(n);
const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));

/**
 * Compute the outcome for one gift. Pure; no I/O.
 *
 * Reference fixture (CHAP-301 smoke test): $100,000 appreciated stock,
 * $20,000 basis, 35% marginal rate, no ruling ->
 * deduction $100,000, appreciation sheltered $80,000 (tax avoided $16,000
 * at the inferred 20% LTCG rate), net cost $49,000.
 */
export function computeGiftOutcome(
  input: GiftInput,
  ruling?: TaxRulingRef | null,
): GiftOutcome {
  const amount = Number(input.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    return ineligible('Enter a gift amount greater than zero.');
  }
  const marginalRatePct = clamp(Number(input.marginalRatePct) || 0, 0, 60);
  const vehicle = GIFT_VEHICLES.find((v) => v.key === input.assetType);
  if (!vehicle) {
    return ineligible('Unknown asset type.');
  }

  const notes = [...VEHICLE_NOTES[vehicle.key]];

  // ── QCD: income exclusion, not a deduction; hard age gate ──
  if (vehicle.key === 'qcd') {
    const age = input.age == null ? null : Number(input.age);
    if (age == null || !Number.isFinite(age)) {
      return ineligible('Enter the donor\'s age; QCDs require age 70.5 or older.', notes);
    }
    if (age < QCD_MIN_AGE) {
      return ineligible('QCD requires age 70.5 or older at the time of the distribution.', notes);
    }
    if (ruling && ruling.percentage != null) {
      notes.push(
        'The educational-space ruling percentage applies to charitable deductions; a QCD is an income exclusion and is shown unscaled. Confirm QCD eligibility of the receiving entity with the foundation.',
      );
    }
    const taxSavings = round((amount * marginalRatePct) / 100);
    return {
      eligible: true,
      ineligibleReason: null,
      deduction: 0,
      deductionAfterRuling: 0,
      rulingPercentageApplied: null,
      incomeExcluded: round(amount),
      taxSavings,
      appreciationSheltered: 0,
      capitalGainsTaxAvoided: 0,
      assumedLtcgRatePct: 0,
      netCost: round(amount) - taxSavings,
      notes,
    };
  }

  // ── Deduction line (all non-QCD vehicles deduct at fair market value) ──
  const deduction = round(amount);
  let deductionAfterRuling = deduction;
  let rulingPercentageApplied: number | null = null;
  if (ruling && ruling.percentage != null && Number.isFinite(Number(ruling.percentage))) {
    const pct = clamp(Number(ruling.percentage), 0, 100);
    rulingPercentageApplied = pct;
    deductionAfterRuling = round((deduction * pct) / 100);
    notes.push(
      `Educational-space deductibility ruling applied: ${pct}% of the gift amount is treated as deductible.`,
    );
  }
  const taxSavings = round((deductionAfterRuling * marginalRatePct) / 100);

  // ── Capital-gains avoidance (basis-bearing vehicles only) ──
  let appreciationSheltered = 0;
  let capitalGainsTaxAvoided = 0;
  let assumedLtcgRatePct = 0;
  if (vehicle.usesBasis) {
    const basis = input.costBasis == null ? null : Number(input.costBasis);
    if (basis == null || !Number.isFinite(basis)) {
      notes.push('Enter the cost basis to estimate capital gains avoided (assumed $0 avoided until then).');
    } else if (basis >= amount) {
      notes.push('Cost basis meets or exceeds the gift value; no capital gain to avoid.');
    } else {
      assumedLtcgRatePct = inferLtcgRatePct(marginalRatePct);
      appreciationSheltered = round(amount - Math.max(0, basis));
      capitalGainsTaxAvoided = round((appreciationSheltered * assumedLtcgRatePct) / 100);
      if (assumedLtcgRatePct > 0) {
        notes.push(
          `Capital-gains avoidance assumes a ${assumedLtcgRatePct}% long-term rate (inferred from the marginal rate; NIIT not modeled).`,
        );
      }
    }
  }

  return {
    eligible: true,
    ineligibleReason: null,
    deduction,
    deductionAfterRuling,
    rulingPercentageApplied,
    incomeExcluded: 0,
    taxSavings,
    appreciationSheltered,
    capitalGainsTaxAvoided,
    assumedLtcgRatePct,
    netCost: deduction - taxSavings - capitalGainsTaxAvoided,
    notes,
  };
}

// ── API shapes (client hook + gift-scenarios route share these) ──

export interface SavedGiftScenario {
  id: string;
  name: string;
  assetType: GiftAssetType;
  inputs: GiftInput;
  outputs: GiftOutcome;
  rulingPercentageApplied: number | null;
  createdByName: string | null;
  createdAt: string;
}

export interface GiftScenariosResponse {
  scenarios: SavedGiftScenario[];
}

export interface TaxRulingResponse {
  ruling: {
    percentage: number;
    dated: string | null;
    attorneyName: string | null;
    basisSummary: string | null;
  } | null;
}
